import { useRef, useState } from 'react';
import type { FileUploadCharacteristic } from '../../schema';
import { useTranslation } from '../../i18n';
import { useCharacteristicSelection, useStoreActions } from '../../state/StoreContext';
import { useWebshopContext } from '../../state/WebshopContext';
import type { UploadedFile } from '../../state/selections';
import { uploadFile, UploadError } from '../../api/uploadFile';

/**
 * Drag-drop / click-to-pick file upload. Each file goes to the admin's
 * storefront asset endpoint (`/api/v1/storefront/assets/files`, see
 * {@link uploadFile}); the answer keeps the legacy `storeasset.php` fields
 * (`id`, `folder`, `type`, `file`) and adds the download `url`. The legacy
 * script never existed on cloud.configento.app, so V3 uploads had nowhere
 * to go before.
 *
 * Native `<input type="file">` + `fetch()` — no Dropzone dependency,
 * fewer kB on the bundle, fewer moving parts in the upload pipeline.
 * Drag-drop is wired via plain `dragover` / `drop` handlers on the
 * shell element. Validation (min/max files, max size, accepted MIME
 * patterns) runs client-side AND server-side; client-side here just
 * spares the round-trip for the obvious cases.
 */
export function FileUploadView({ characteristic }: { characteristic: FileUploadCharacteristic }) {
    const { t } = useTranslation();
    const webshop = useWebshopContext();
    const selection = useCharacteristicSelection(characteristic.id);
    const { setFiles } = useStoreActions();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);

    const files: ReadonlyArray<UploadedFile> = selection?.kind === 'files' ? selection.files : [];

    const maxBytes = parseMaxSize(characteristic.maxFilesize);
    const acceptList = parseAcceptedFiles(characteristic.acceptedFiles);

    const constraints: string[] = [];
    if (characteristic.minFiles != null) constraints.push(t('fileupload.min_files', { n: characteristic.minFiles }));
    if (characteristic.maxFiles != null) constraints.push(t('fileupload.max_files', { n: characteristic.maxFiles }));
    if (characteristic.maxFilesize)      constraints.push(t('fileupload.max_size', { size: sizeLabel(characteristic.maxFilesize) }));
    if (characteristic.acceptedFiles)    constraints.push(characteristic.acceptedFiles);

    async function acceptDrop(picked: FileList | null): Promise<void> {
        if (!picked || picked.length === 0) return;
        setError(null);

        // Client-side validation against the documented constraints. The
        // server validates too — we just spare the round-trip and give
        // the user immediate feedback on the obvious cases.
        const candidates: File[] = [];
        for (let i = 0; i < picked.length; i++) {
            const f = picked[i];
            if (!f) continue;
            if (maxBytes !== null && f.size > maxBytes) {
                setError(t('fileupload.error_too_big', { name: f.name, max: sizeLabel(characteristic.maxFilesize ?? '') }));
                return;
            }
            if (acceptList.length > 0 && !matchesAccept(f, acceptList)) {
                setError(t('fileupload.error_wrong_type', { name: f.name }));
                return;
            }
            candidates.push(f);
        }
        const newTotal = files.length + candidates.length;
        if (characteristic.maxFiles != null && newTotal > characteristic.maxFiles) {
            setError(t('fileupload.error_too_many', { max: characteristic.maxFiles }));
            return;
        }

        if (!webshop) {
            setError(t('fileupload.error_no_endpoint'));
            return;
        }

        setBusy(true);
        const uploaded: UploadedFile[] = [...files];
        try {
            for (const file of candidates) {
                const stored = await uploadFile(
                    webshop,
                    file,
                    `${characteristic.altTitle || characteristic.id}_${slug(file.name)}`,
                );
                uploaded.push({
                    id:           stored.id,
                    folder:       stored.folder,
                    type:         stored.type,
                    file:         stored.file,
                    url:          stored.url,
                    originalName: file.name,
                    size:         file.size,
                });
            }
        } catch (err) {
            setError(err instanceof UploadError && err.status > 0
                ? t('fileupload.error_server', { code: err.status })
                : t('fileupload.error_network', { msg: (err as Error).message }));
        } finally {
            // Files that made it before a failure stay picked.
            setFiles(characteristic.id, uploaded);
            setBusy(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    }

    function removeFile(idx: number): void {
        setFiles(characteristic.id, files.filter((_, i) => i !== idx));
    }

    return (
        <div
            className="cfg-fileupload"
            role="region"
            aria-label={t('fileupload.dropzone')}
            data-configento-busy={busy || undefined}
            data-configento-drag-over={dragOver || undefined}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                void acceptDrop(e.dataTransfer.files);
            }}
        >
            <input
                ref={inputRef}
                type="file"
                multiple={characteristic.maxFiles !== 1}
                accept={characteristic.acceptedFiles ?? undefined}
                style={{ display: 'none' }}
                onChange={(e) => void acceptDrop(e.target.files)}
            />
            <button
                type="button"
                className="cfg-fileupload__pick"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
            >
                {busy ? t('fileupload.uploading') : t('fileupload.pick_or_drop')}
            </button>
            {constraints.length > 0 && (
                <div className="cfg-fileupload__constraints">{constraints.join(' · ')}</div>
            )}
            {error && (
                <div className="cfg-fileupload__error" role="alert">{error}</div>
            )}
            {files.length > 0 && (
                <ul className="cfg-fileupload__list">
                    {files.map((f, i) => (
                        <li key={`${f.id}-${f.file}-${i}`} className="cfg-fileupload__item">
                            <span className="cfg-fileupload__name" title={f.originalName}>
                                {f.originalName}
                            </span>
                            <span className="cfg-fileupload__size">{formatBytes(f.size)}</span>
                            <button
                                type="button"
                                className="cfg-fileupload__remove"
                                aria-label={t('fileupload.remove', { name: f.originalName })}
                                onClick={() => removeFile(i)}
                            >
                                ×
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/**
 * `5MB` / `512KB` / `2 MB` → number-of-bytes, or null for "no limit". A bare
 * number is megabytes: the admin field is "Max. Dateigröße (MB)", and legacy
 * hands it to Dropzone's `maxFilesize`, which counts in MB as well.
 */
function parseMaxSize(raw: string | undefined): number | null {
    if (!raw) return null;
    const m = raw.trim().match(/^(\d+(?:\.\d+)?)\s*([kmg]?b?)$/i);
    if (!m) return null;
    const n = Number(m[1]);
    if (!Number.isFinite(n)) return null;
    const unit = (m[2] ?? '').toLowerCase();
    const mult =
        unit.startsWith('g') ? 1024 ** 3 :
        unit.startsWith('m') ? 1024 ** 2 :
        unit.startsWith('k') ? 1024       :
        unit === 'b'         ? 1          :
        1024 ** 2;
    return Math.floor(n * mult);
}

/** `5` → `5 MB`; values that carry a unit stay as written. */
function sizeLabel(raw: string): string {
    return /^\s*\d+(?:[.,]\d+)?\s*$/.test(raw) ? `${raw.trim()} MB` : raw;
}

function parseAcceptedFiles(raw: string | undefined): string[] {
    if (!raw) return [];
    return raw.split(',').map((s) => s.trim()).filter(Boolean).map((s) =>
        s.startsWith('.') || s.includes('/') ? s : `.${s}`,
    );
}

function matchesAccept(file: File, list: string[]): boolean {
    for (const entry of list) {
        if (entry.startsWith('.')) {
            if (file.name.toLowerCase().endsWith(entry.toLowerCase())) return true;
        } else if (entry.endsWith('/*')) {
            const prefix = entry.slice(0, -1);
            if (file.type.startsWith(prefix)) return true;
        } else {
            if (file.type === entry) return true;
        }
    }
    return false;
}

function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} kB`;
    if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
    return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

function slug(name: string): string {
    return name.replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 64);
}
